class TdIngredients extends HTMLElement {
  connectedCallback() {
    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
    this.handleHashChange = this.handleHashChange.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
    this.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
    window.addEventListener('hashchange', this.handleHashChange);

    this.openFromHash();
    this.setRovingTabindex();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
    window.removeEventListener('hashchange', this.handleHashChange);
  }

  handleHashChange() {
    this.openFromHash();
  }

  /**
   * Resolves a URL fragment to a tab trigger, matched case insensitively
   * against every fragment the tab answers to.
   *
   * @param {string} hash - A location fragment, with or without the leading #.
   * @returns {HTMLElement|null}
   */
  tabFromHash(hash) {
    const wanted = TdIngredients.slug(decodeURIComponent(String(hash || '').replace(/^#/, '')));
    if (!wanted) return null;

    return (
      [...this.querySelectorAll('[data-td-tab-trigger]')].find((trigger) =>
        TdIngredients.anchorsFor(trigger).includes(wanted)
      ) || null
    );
  }

  /**
   * Every fragment a tab answers to. The product handle is included so a link
   * built from a product, such as the one the ingredients dialog renders, opens
   * that product's tab without the merchant having to set an anchor.
   *
   * @param {HTMLElement} trigger
   * @returns {string[]}
   */
  static anchorsFor(trigger) {
    return [
      TdIngredients.anchorFor(trigger),
      TdIngredients.slug(trigger.dataset.tdTabProduct)
    ].filter(Boolean);
  }

  /**
   * A tab's anchor: the merchant's own value when one is set, otherwise derived
   * from the block key. This is the single definition of a tab's fragment, used
   * both to match an incoming link and to write one on click, so the two can
   * never disagree.
   *
   * @param {HTMLElement} trigger
   * @returns {string}
   */
  static anchorFor(trigger) {
    return (
      TdIngredients.slug(trigger.dataset.tdTabAnchor) ||
      TdIngredients.slug(trigger.dataset.tdTabBlock)
    );
  }

  /**
   * Normalises a value into a URL fragment: lowercase, a leading `tab_` dropped,
   * everything else hyphenated. Applied to both sides of a comparison so an
   * incoming link is matched case and separator insensitively.
   *
   * @param {string} value
   * @returns {string}
   */
  static slug(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/^tab[-_]/, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Reflects the open tab in the address bar so it is always a shareable deep
   * link. `replaceState` rather than assigning `location.hash`, which would add
   * a history entry per click and fire `hashchange`, re-entering openFromHash
   * for a tab that is already open.
   *
   * @param {HTMLElement} trigger
   */
  syncHash(trigger) {
    const slug = TdIngredients.anchorFor(trigger);
    if (!slug || typeof window.history?.replaceState !== 'function') return;

    const { pathname, search } = window.location;
    window.history.replaceState(window.history.state, '', `${pathname}${search}#${slug}`);
  }

  /**
   * Opens the tab named by the current fragment. Scrolling is left to the
   * browser: a fragment matching a real element id moves the page on its own,
   * and a slug that matches no id leaves the visitor where they landed.
   */
  openFromHash() {
    const trigger = this.tabFromHash(window.location.hash);
    if (!trigger) return false;

    this.activateTab(trigger.dataset.tdTabTarget);
    return true;
  }

  handleClick(event) {
    const tabTrigger = event.target.closest('[data-td-tab-trigger]');
    if (tabTrigger && this.contains(tabTrigger)) {
      this.activateTab(tabTrigger.dataset.tdTabTarget);
      this.syncHash(tabTrigger);
      return;
    }

    const ingredientTrigger = event.target.closest('[data-td-ingredient-trigger]');
    if (ingredientTrigger && this.contains(ingredientTrigger)) {
      this.activateIngredient(ingredientTrigger.dataset.tdGroupId, ingredientTrigger.dataset.tdIngredientTarget);
    }
  }

  /**
   * Arrow, Home and End move across the tablist and open the tab they land on,
   * which is what the tabs pattern requires of anything carrying `role="tab"`.
   * Buttons alone are reachable but leave a screen reader user pressing keys
   * that do nothing.
   *
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const trigger = event.target.closest('[data-td-tab-trigger]');
    if (!trigger || !this.contains(trigger)) return;

    const triggers = [...this.querySelectorAll('[data-td-tab-trigger]')];
    const last = triggers.length - 1;
    const current = triggers.indexOf(trigger);
    let next = null;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = current === last ? 0 : current + 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = current === 0 ? last : current - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;

    if (next === null) return;

    event.preventDefault();
    this.activateTab(triggers[next].dataset.tdTabTarget);
    this.syncHash(triggers[next]);
    triggers[next].focus();
  }

  /**
   * A roving tabindex over the tabs, so Tab moves out of the list into the open
   * panel instead of through every tab. Applied from JS rather than the markup:
   * without the component the arrow keys would not work either, and every tab
   * should stay reachable in that case.
   */
  setRovingTabindex() {
    this.querySelectorAll('[data-td-tab-trigger]').forEach((trigger) => {
      trigger.tabIndex = trigger.getAttribute('aria-selected') === 'true' ? 0 : -1;
    });
  }

  handleChange(event) {
    const ingredientSelect = event.target.closest('[data-td-ingredient-select]');
    if (!ingredientSelect || !this.contains(ingredientSelect)) return;

    this.activateIngredient(ingredientSelect.dataset.tdGroupId, ingredientSelect.value);
  }

  handleBlockSelect(event) {
    const block = event.target.closest('[data-td-tab-panel]');
    if (!block || !this.contains(block)) return;

    const trigger = this.querySelector(`[data-td-tab-trigger][data-td-tab-block="${CSS.escape(block.dataset.tdTabBlock)}"]`);
    if (trigger) this.activateTab(trigger.dataset.tdTabTarget);
  }

  handleBlockDeselect() {}

  activateTab(targetId) {
    this.querySelectorAll('[data-td-tab-trigger]').forEach((trigger) => {
      const isActive = trigger.dataset.tdTabTarget === targetId;
      trigger.classList.toggle('td-ingredients__tab--active', isActive);
      trigger.setAttribute('aria-selected', String(isActive));
      trigger.tabIndex = isActive ? 0 : -1;
    });

    this.querySelectorAll('[data-td-tab-panel]').forEach((panel) => {
      panel.hidden = panel.id !== targetId;
    });
  }

  activateIngredient(groupId, targetId) {
    this.querySelectorAll(`[data-td-ingredient-trigger][data-td-group-id="${CSS.escape(groupId)}"]`).forEach((trigger) => {
      const isActive = trigger.dataset.tdIngredientTarget === targetId;
      trigger.classList.toggle('td-ingredients__ingredient-trigger--active', isActive);
      if (isActive) trigger.setAttribute('aria-current', 'true');
      else trigger.removeAttribute('aria-current');
    });

    this.querySelectorAll(`[data-td-ingredient-detail][data-td-group-id="${CSS.escape(groupId)}"]`).forEach((detail) => {
      detail.hidden = detail.id !== targetId;
    });

    const select = this.querySelector(`[data-td-ingredient-select][data-td-group-id="${CSS.escape(groupId)}"]`);
    if (select && select.value !== targetId) select.value = targetId;
  }
}

if (!customElements.get('td-ingredients')) {
  customElements.define('td-ingredients', TdIngredients);
}
